import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Briefcase } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';

export default function JobsPage() {
  const { user } = useContext(AuthContext);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canViewJobs = hasPermission('JOB_VIEW');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    expiry_date: '',
    link: ''
  });

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }

    if (!canViewJobs) {
      setLoading(false);
      return;
    }
    fetchJobs();
  }, [canViewJobs, permissionsLoading]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingJob) {
        await axios.put(`/jobs/${editingJob.id}`, formData);
        toast.success('Offerta aggiornata!');
      } else {
        await axios.post('/jobs', formData);
        toast.success('Offerta pubblicata!');
      }
      setDialogOpen(false);
      setEditingJob(null);
      setFormData({ title: '', company: '', description: '', expiry_date: '', link: '' });
      fetchJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'operazione'));
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company: job.company,
      description: job.description,
      expiry_date: job.deadline_date ? job.deadline_date.split('T')[0] : '',
      link: job.link || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`/jobs/${jobId}`);
      toast.success('Offerta eliminata');
      fetchJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione'));
    }
  };

  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">Caricamento...</div>
      </div>
    );
  }

  if (!canViewJobs) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare le opportunità." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="jobs-page">
      <Navbar />

      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-6 w-6" />
              <h1 className="text-3xl font-bold" data-testid="page-title">Offerte di lavoro / Opportunità</h1>
            </div>
          </div>
          <div>
          {hasPermission('JOB_CREATE') && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingJob(null);
                setFormData({ title: '', company: '', description: '', expiry_date: '', link: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-black text-white hover:bg-gray-800" data-testid="create-job-button">
                  + Nuova offerta
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="job-dialog">
              <DialogHeader>
                <DialogTitle>{editingJob ? 'Modifica offerta' : 'Pubblica offerta di lavoro'}</DialogTitle>
                <DialogDescription>Condividi un'opportunità lavorativa con i membri</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Titolo posizione</Label>
                  <Input
                    id="title"
                    data-testid="job-title-input"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="company">Azienda (facoltativo)</Label>
                  <Input
                    id="company"
                    data-testid="job-company-input"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Nome dell'azienda"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione *</Label>
                  <Textarea
                    id="description"
                    data-testid="job-description-input"
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Descrivi l'opportunità..."
                  />
                </div>
                <div>
                  <Label htmlFor="expiry_date">Data di scadenza (facoltativa)</Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="link">Link (facoltativo)</Label>
                  <Input
                    id="link"
                    type="url"
                    placeholder="https://..."
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                  />
                </div>
                <Button type="submit" className="bg-black text-white hover:bg-gray-800 w-full" data-testid="submit-job-button">
                  {editingJob ? 'Aggiorna' : 'Pubblica'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 text-gray-600" data-testid="no-jobs-message">
            Nessuna offerta di lavoro disponibile.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {jobs.map((job) => (
              <Card key={job.id} className="border border-gray-200" data-testid="job-card">
                <CardHeader>
                  <CardTitle>{job.title}</CardTitle>
                  {job.company && <CardDescription>{job.company}</CardDescription>}
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">{job.description}</p>
                  
                  {job.deadline_date && (
                    <p className="text-sm text-gray-600 mb-2">
                      📅 Scadenza: {new Date(job.deadline_date).toLocaleDateString('it-IT')}
                    </p>
                  )}
                  
                  {job.link && (
                    <a 
                      href={job.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm flex items-center gap-1 mb-4"
                    >
                      🔗 Vai al link
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      Pubblicata: {new Date(job.created_at).toLocaleDateString('it-IT')}
                    </p>
                    <div className="flex gap-2">
                      {/* Mostra pulsante Modifica se: ha JOB_EDIT_ALL o (ha JOB_EDIT_OWN e è il proprietario) */}
                      {(hasPermission('JOB_EDIT_ALL') || (hasPermission('JOB_EDIT_OWN') && job.is_own)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(job)}
                          className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                          data-testid="edit-job-button"
                        >
                          Modifica
                        </Button>
                      )}
                      
                      {/* Mostra pulsante Elimina se: ha JOB_DELETE_ALL o (ha JOB_DELETE_OWN e è il proprietario) */}
                      {(hasPermission('JOB_DELETE_ALL') || (hasPermission('JOB_DELETE_OWN') && job.is_own)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(job.id)}
                          className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                          data-testid="delete-job-button"
                        >
                          Elimina
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
